import React, { useCallback, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

import usePopover from "components/common/usePopover";
import { disabledStatusOptions, selectableStatusesFor } from "../helper/appointmentConstants";
import UserService from "modules/user/service/UserService";

/**
 * The coloured status pill with a dropdown. Hand-rolled because phr has no UI
 * kit — a native <select> can't carry the pill's tone or the dot.
 *
 * Positioning and dismissal live in usePopover, which also documents WHY the
 * menu must be portalled rather than nested (.appointments-table clips it).
 */
const StatusSelect = ({
  value,
  tone,
  label,
  options,
  labels,
  tones,
  disabled,
  readOnly,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  /* Derived HERE rather than passed in, so all four screens that render this
     control get the same rule without threading a prop through each of them —
     and so a new caller cannot forget it. */
  const blocked = disabledStatusOptions(value);
  /* WHICH statuses are offered depends on who is signed in — a nutritionist's
     list differs from a doctor's (selectableStatusesFor). Derived here, like
     `blocked`, so every screen that renders this control gets the same rule
     and none can pass a stale list. `options` overrides it. */
  const offered = useMemo(
    () => options || selectableStatusesFor(new UserService().doctorCategory()),
    [options]
  );
  const { triggerRef, menuRef, menuStyle } = usePopover(open, close);

  const pick = (next) => {
    setOpen(false);
    if (next !== value) onChange(next);
  };

  // Terminal status: the server won't move it anywhere useful, so render the
  // pill as plain text rather than a control that promises an action.
  if (readOnly) {
    return (
      <span className={`status-pill is-readonly tone-${tone}`}>
        <span className="dot" aria-hidden="true" />
        <span className="status-text">{label}</span>
      </span>
    );
  }

  return (
    <div className="status-select">
      <button
        ref={triggerRef}
        type="button"
        className={`status-pill tone-${tone}`}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="dot" aria-hidden="true" />
        <span className="status-text">{label}</span>
        <span className="caret" aria-hidden="true">
          &#9662;
        </span>
      </button>

      {open &&
        ReactDOM.createPortal(
          <ul className="status-menu" role="listbox" ref={menuRef} style={menuStyle}>
            {offered.map((opt) => (
              <li key={opt}>
                <button
                  type="button"
                  role="option"
                  aria-selected={opt === value}
                  aria-disabled={blocked.indexOf(opt) >= 0}
                  disabled={blocked.indexOf(opt) >= 0}
                  /* "progress" is bridge's own `default:` arm, and the tone set
                     no longer has a "muted" — it was renamed with the rest when
                     the chips moved to bridge's four buckets. */
                  className={`status-option tone-${tones[opt] || "progress"}${
                    opt === value ? " is-selected" : ""
                  }${blocked.indexOf(opt) >= 0 ? " is-blocked" : ""}`}
                  onClick={() => pick(opt)}
                >
                  <span className="dot" aria-hidden="true" />
                  {labels[opt]}
                </button>
              </li>
            ))}
          </ul>,
          document.body
        )}
    </div>
  );
};

StatusSelect.propTypes = {
  value: PropTypes.number,
  tone: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  /** Override. By default: the signed-in user's list (selectableStatusesFor). */
  options: PropTypes.arrayOf(PropTypes.number),
  labels: PropTypes.object.isRequired,
  tones: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  /** Terminal status — render a plain pill, no dropdown. */
  readOnly: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

export default StatusSelect;
